import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getCommentsSettings, manageCommentsSettings } from 'services/actions/commentsService';
import Modal from 'components/actions/ActionSettingsModal';
import Spinner from 'components/common/Spinner';
import { toast } from 'sonner';
import { useTheme } from 'contexts/ThemeContext';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';
import type { ActionSettingsSaveData } from 'components/actions/ActionSettingsModal';

interface CommentsSettingsProps {
  selectedUser: string;
}

interface Template {
  template_name: string;
  template_data: string;
}

type SettingsValue = boolean | string[] | Template[];

interface Setting {
  _id: string;
  action_id: string;
  settings_type: string;
  description: string;
  settings_value: SettingsValue;
  is_active?: boolean;
  category_enabled?: boolean;
}

const asStringArray = (v: SettingsValue): string[] => (Array.isArray(v) ? (v as string[]) : []);
const asTemplateArray = (v: SettingsValue): Template[] => (Array.isArray(v) ? (v as Template[]) : []);

interface SettingsCategory {
  title: string;
  options: Setting[];
}

const CommentsSettings: React.FC<CommentsSettingsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKeyword, setEditingKeyword] = useState<{ index: number; optionIndex: number }>({ index: -1, optionIndex: -1 });
  const [settingsType, setSettingsType] = useState<string>('');
  const [settingsSubType, setSettingsSubType] = useState<string>('');
  const [oldKeywordName, setOldKeywordName] = useState<string>('');
  const [oldHeader, setOldHeader] = useState<string>('');
  const [oldValue, setOldValue] = useState<string>('');
  const [actionID, setActionID] = useState<string>('');

  const [status, setStatus] = useState<string>('Activate');
  const [template, setTemplate] = useState<string>('');
  const [templateActionId, setTemplateActionId] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const closeModal = () => setModalOpen(false);

  // Design tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const sectionHeaderColor = isDark ? '#E2E8F0' : '#314158';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const switchOnBg = isDark ? '#51A2FF' : '#2B7FFF';
  const switchKnob = isDark ? '#0F172B' : '#FFFFFF';
  const switchOffBg = isDark ? '#314158' : '#E2E8F0';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const warningBg = isDark ? '#422006' : '#FFFBEB';
  const warningBorder = isDark ? '#92400E' : '#FDE68A';
  const warningText = isDark ? '#FCD34D' : '#92400E';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const disabledLabelColor = isDark ? '#62748E' : '#90A1B9';
  const variableChipBg = isDark ? '#1D293D' : '#F1F5F9';
  const variableChipText = isDark ? '#CAD5E2' : '#45556C';
  const variableChipHoverBg = isDark ? '#162456' : '#EFF6FF';
  const iconButtonBg = isDark ? '#162456' : '#EFF6FF';
  const iconButtonColor = isDark ? '#51A2FF' : '#2B7FFF';
  const dangerBg = isDark ? '#3B0A0A' : '#FEF2F2';
  const dangerColor = isDark ? '#FF637E' : '#EF4444';
  const itemBg = isDark ? '#1D293D' : '#F8FAFC';
  const itemBorder = isDark ? '#314158' : '#E2E8F0';
  const itemText = isDark ? '#E2E8F0' : '#314158';
  const editBtnBg = isDark ? '#1D293D' : '#F8FAFC';
  const editBtnText = isDark ? '#CAD5E2' : '#45556C';

  // List of available Spintax variables
  const availableVariables = [
    '{{firstname|full_name}}',
    '@{{username}}',
    '{Hi|Hey|Hello}',
    '{Amazing|Incredible|Wow}',
    '{Beautiful|Gorgeous|Stunning}',
    '{Nice|Cool|Dope}',
    '{Slay|Killed it|Go off}',
    '{🔥|✨|💯}',
    '{Keep going|You got this|Shine}',
    '{Bet|No cap|Big mood}',
    '{Too cute|Yasss|Obsessed}',
    '{Love this|So proud|Gem}',
    '{Congrats|Nailed it|Bravo}'
  ];


  const saveSettings = async (data: ActionSettingsSaveData | string) => {
    try {
      const isActive = settingsSubType === 'remove' ? false : true;
      const d = data as ActionSettingsSaveData;
      switch (settingsType) {
        case "toggle":
          await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, data, isActive);
          fetchSettings();
          return "General";

        case "keyword_list":
          switch (settingsSubType) {
            case "add":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, d.keywordName, isActive);
              fetchSettings();
              return "Adding Keyword";
            case "edit":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { old: d.oldKeywordName, new: d.keywordName }, isActive);
              fetchSettings();
              return "Editing Keyword";
            case "remove":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, data, isActive);
              fetchSettings();
              return "Deleting Keyword";
            default:
              return "Unknown Keyword Action";
          }

        case "template_list":
          switch (settingsSubType) {
            case "add":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.header, template_data: d.value }, isActive);
              fetchSettings();
              return "Adding Template";
            case "edit":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.oldHeader, template_data: d.value }, isActive);
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "template_list")?.options?.length ?? 0) === 1) {
                toast.error(t('commentsSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.oldHeader }, isActive);
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        case "text_list":
          switch (settingsSubType) {
            case "add":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.header, template_data: d.value }, isActive);
              fetchSettings();
              return "Adding Template";
            case "edit":
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.oldHeader, template_data: d.value }, isActive);
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) === 1) {
                toast.error(t('commentsSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: d.oldHeader }, isActive);
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        default:
          return "Unknown Settings Type";
      }
    } catch (err: unknown) {
      logger.error("Error saving settings:", err);
      setError(getErrorMessage(err, 'Failed to save settings'));
    }
  };

  const fetchSettings = async () => {
    try {
      if (!selectedUser) throw new Error("No user selected");
      const response = (await getCommentsSettings(selectedUser)) as Setting[];
      if (Array.isArray(response)) {
        setSettings(response);

        const templateOption = response.find(item =>
          item.settings_type === "template_list" &&
          Array.isArray(item.settings_value) &&
          item.settings_value.length > 0
        );

        if (templateOption) {
          setTemplateActionId(templateOption.action_id);
          const tplList = asTemplateArray(templateOption.settings_value);
          if (tplList[0]) {
            setTemplate(tplList[0].template_data || '');
            setOldHeader(tplList[0].template_name || '');
            setStatus(templateOption.is_active ? 'Activate' : 'Deactivate');
          }
        }
      } else {
        throw new Error("Invalid settings data format");
      }
      setLoading(false);
    } catch (err: unknown) {
      logger.error("Error fetching settings:", err);
      setError(getErrorMessage(err, 'Failed to fetch settings'));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchSettings();
    }
  }, [selectedUser]);

  const toggleSetting = async (actionId: string, category: string, option: Setting, newValue: boolean) => {
    try {
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: newValue }
          : item
      ));

      await manageCommentsSettings(selectedUser, actionId, category, "edit", newValue, newValue);
    } catch (err: unknown) {
      logger.error("Error toggling setting:", err);
      setError(getErrorMessage(err, 'Failed to toggle setting'));
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: !newValue }
          : item
      ));
    }
  };

  const toggleStatus = async () => {
    const newStatus = status === 'Activate' ? 'Deactivate' : 'Activate';
    const isActive = newStatus === 'Activate';

    try {
      setStatus(newStatus);

      setSettings(prev => prev.map(item =>
        item.action_id === templateActionId && item.settings_type === "template_list"
          ? { ...item, is_active: isActive }
          : item
      ));

      await manageCommentsSettings(
        selectedUser,
        templateActionId,
        "template_list",
        "edit",
        { template_name: oldHeader, template_data: template },
        isActive
      );

      toast.success(t('commentsSettings.messages.statusUpdated', { status: newStatus }));
    } catch (error: unknown) {
      logger.error('Error toggling status:', error);
      setStatus(status);
      toast.error(t('commentsSettings.errors.statusUpdateFailed'));
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSettings(prev => prev.map(item =>
        item.action_id === templateActionId && item.settings_type === "template_list"
          ? {
              ...item,
              settings_value: asTemplateArray(item.settings_value).map((tpl, idx: number) =>
                idx === 0 ? { ...tpl, template_data: template } : tpl
              )
            }
          : item
      ));

      await manageCommentsSettings(
        selectedUser,
        templateActionId,
        "template_list",
        "edit",
        { template_name: oldHeader, template_data: template },
        true
      );

      toast.success(t('commentsSettings.messages.templateSaved'));
    } catch (error: unknown) {
      logger.error('Error saving template:', error);
      toast.error(t('commentsSettings.errors.templateSaveFailed'));
    }
  };

  const handleVariableClick = (variable: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const cursorPos = textarea.selectionStart;
      const textBefore = template.substring(0, cursorPos);
      const textAfter = template.substring(cursorPos);
      const newTemplate = `${textBefore}${variable}${textAfter}`;
      setTemplate(newTemplate);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = cursorPos + variable.length;
        textarea.focus();
      }, 0);
    }
  };

  const handleKeywordChange = (optionIndex: number, keyIndex: number, value: string) => {
    setSettings(prev => prev.map((item, idx) => {
      if (idx === optionIndex) {
        const newValues: string[] = [...asStringArray(item.settings_value)];
        newValues[keyIndex] = value;
        return { ...item, settings_value: newValues };
      }
      return item;
    }));
  };

  // Custom toggle switch component
  const ToggleSwitch: React.FC<{ checked: boolean; disabled?: boolean; onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void }> = ({ checked, disabled, onChange }) => (
    <label style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
      />
      <div
        style={{
          width: 40,
          height: 20,
          borderRadius: 999,
          background: checked ? switchOnBg : switchOffBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: checked ? 'flex-end' : 'flex-start',
          padding: 2,
          transition: 'background 0.2s ease, justify-content 0.2s ease',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: 999,
            background: switchKnob,
            transition: 'all 0.2s ease',
          }}
        />
      </div>
    </label>
  );

  if (loading) {
    return <Spinner size="large" text={t('commentsSettings.loading')} fullPage />;
  }

  if (error) {
    return (
      <div style={{
        background: mainBg,
        borderRadius: 8,
        padding: 32,
        maxWidth: 448,
        margin: '32px auto',
        textAlign: 'center',
      }}>
        <div style={{
          width: 64,
          height: 64,
          background: dangerBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px auto',
        }}>
          <span style={{ color: dangerColor, fontSize: 24 }}>!</span>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: sectionHeaderColor, marginBottom: 8 }}>{t('commentsSettings.errors.loadingError')}</h3>
        <p style={{ fontSize: 12, color: descriptionColor, marginBottom: 16 }}>{error}</p>
        <button
          style={{
            padding: '8px 16px',
            background: buttonBg,
            color: '#FFFFFF',
            borderRadius: 6,
            border: 'none',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onClick={() => window.location.reload()}
        >
          {t('commentsSettings.buttons.retry')}
        </button>
      </div>
    );
  }

  const settingsCategories: SettingsCategory[] = [...new Set(settings.map(item => item.settings_type))]
    .filter(settingsType => settingsType !== 'template_list' || status === 'Activate')
    .map(settingsType => {
      const filteredSettings = settings.filter(item => item.settings_type === settingsType);
      return {
        title: settingsType,
        options: filteredSettings
      };
    });

  const templateSettings = settings.find(item => item.settings_type === "template_list");

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={saveSettings}
          selectedSettingsType={settingsType}
          selectedSettingsSubType={settingsSubType}
          selectedOldKeywordName={oldKeywordName}
          selectedOldHeader={oldHeader}
          selectedOldValue={oldValue}
        />
      )}

      {settingsCategories.map((category, index) => (
        <div key={index}>
          {/* Section header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: `1px solid ${sectionBorder}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: sectionHeaderColor, margin: 0 }}>
              {{
                toggle: t('commentsSettings.sections.generalSettings'),
                keyword_list: t('commentsSettings.sections.keywords'),
                template_list: t('commentsSettings.sections.commentTemplate'),
                text_list: t('commentsSettings.sections.textLists')
              }[category.title] || category.title}
            </h3>
          </div>

          {/* Toggle settings */}
          {category.title === "toggle" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(category.options.some(opt => opt.category_enabled === false) || (templateSettings && templateSettings.category_enabled === false)) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: 12,
                  background: warningBg,
                  border: `1px solid ${warningBorder}`,
                  borderRadius: 8,
                }}>
                  <p style={{ fontSize: 12, color: warningText, margin: 0 }}>{t('commentsSettings.warnings.categoryDisabled')}</p>
                </div>
              )}
              {category.options.map((option, optIndex) => {
                const isReadOnly = option.category_enabled === false;
                return (
                  <div key={optIndex} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: isReadOnly ? disabledLabelColor : labelColor,
                      }}>{option.description}</span>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: descriptionColor,
                      }}>{t('commentsSettings.descriptions.toggleSetting', 'Toggle this setting on or off.')}</span>
                    </div>
                    <ToggleSwitch
                      checked={typeof option.settings_value === 'boolean' ? option.settings_value : Boolean(option.settings_value)}
                      disabled={isReadOnly}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isReadOnly) return;
                        const newValue = e.target.checked;
                        setActionID(option.action_id);
                        setSettingsType("toggle");
                        setSettingsSubType("edit");
                        toggleSetting(option.action_id, "toggle", option, newValue);
                      }}
                    />
                  </div>
                );
              })}
              {/* Status toggle using template settings description */}
              {templateSettings && (() => {
                const isReadOnly = templateSettings.category_enabled === false;
                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: isReadOnly ? disabledLabelColor : labelColor,
                      }}>{templateSettings.description}</span>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: descriptionColor,
                      }}>{t('commentsSettings.descriptions.activation', 'Turn on or off. When disabled, no comments will be posted.')}</span>
                    </div>
                    <ToggleSwitch
                      checked={status === 'Activate'}
                      disabled={isReadOnly}
                      onChange={isReadOnly ? undefined : toggleStatus}
                    />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Keyword list settings */}
          {category.title === "keyword_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: labelColor }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        background: iconButtonBg,
                        color: iconButtonColor,
                        borderRadius: 6,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => {
                        setSettingsType('keyword_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {asStringArray(option.settings_value).map((keyword: string, keyIndex: number) => (
                      <div key={keyIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 12,
                        background: itemBg,
                        borderRadius: 8,
                        border: `1px solid ${itemBorder}`,
                      }}>
                        <div style={{ flex: 1 }}>
                          {editingKeyword.optionIndex === optIndex && editingKeyword.index === keyIndex ? (
                            <input
                              type="text"
                              value={keyword}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleKeywordChange(
                                settings.findIndex(s => s._id === option._id),
                                keyIndex,
                                e.target.value
                              )}
                              onBlur={() => setEditingKeyword({ index: -1, optionIndex: -1 })}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: 12,
                                border: `1px solid ${inputBorder}`,
                                borderRadius: 4,
                                background: inputBg,
                                color: inputText,
                                outline: 'none',
                              }}
                              autoFocus
                            />
                          ) : (
                            <span style={{ fontSize: 12, color: itemText }}>{keyword}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              background: editBtnBg,
                              color: editBtnText,
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              background: dangerBg,
                              color: dangerColor,
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('remove');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            X
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Template list settings */}
          {category.title === "template_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: labelColor, marginBottom: 8 }}>{t('commentsSettings.labels.definedTemplate')}</label>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '12px',
                      background: infoBg,
                      border: `1px solid ${infoBorder}`,
                      borderRadius: '8px',
                      marginBottom: 16,
                    }}>
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: infoText }}>
                          {t('commentsSettings.infoMessages.keepGeneralText')}
                        </span>
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: infoText }}>
                          {t('commentsSettings.infoMessages.threeVariablesRequired')}
                        </span>
                    </div>

                    <textarea
                      ref={textareaRef}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: 12,
                        border: `1px solid ${inputBorder}`,
                        borderRadius: 6,
                        background: inputBg,
                        color: inputText,
                        outline: 'none',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                      }}
                      placeholder={t('commentsSettings.placeholders.writeComment')}
                      value={template}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTemplate(e.target.value)}
                      rows={6}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: labelColor, marginBottom: 4 }}>{t('commentsSettings.labels.availableVariables')}</label>
                    <p style={{ fontSize: 14, color: descriptionColor, marginBottom: 12, marginTop: 0 }}>{t('commentsSettings.hints.clickToInsert')}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {availableVariables.map((variable, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '8px 12px',
                            background: variableChipBg,
                            color: variableChipText,
                            borderRadius: 6,
                            fontSize: 12,
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onClick={() => handleVariableClick(variable)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background = variableChipHoverBg;
                            (e.currentTarget as HTMLElement).style.color = iconButtonColor;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background = variableChipBg;
                            (e.currentTarget as HTMLElement).style.color = variableChipText;
                          }}
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <button
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                        color: '#FFFFFF',
                        background: buttonBg,
                        transition: 'all 0.2s ease',
                      }}
                      onClick={handleSaveDraft}
                    >
                      {t('commentsSettings.buttons.saveTemplate')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Text list settings */}
          {category.title === "text_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: labelColor }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        background: iconButtonBg,
                        color: iconButtonColor,
                        borderRadius: 6,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => {
                        setSettingsType('text_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {asTemplateArray(option.settings_value).map((template, tplIndex: number) => (
                      <div key={tplIndex} style={{
                        background: cardBg,
                        borderRadius: 8,
                        border: `1px solid ${cardBorder}`,
                        overflow: 'hidden',
                      }}>
                        <div style={{ padding: 16 }}>
                          <p style={{ fontSize: 14, fontWeight: 500, color: itemText, marginBottom: 8, marginTop: 0 }}>{template.template_name}</p>
                          <p style={{ fontSize: 12, color: descriptionColor, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                            {template.template_data}
                          </p>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          background: mainBg,
                          borderTop: `1px solid ${cardBorder}`,
                        }}>
                          <button
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 12,
                              color: editBtnText,
                              background: editBtnBg,
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => {
                              setSettingsType('text_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldHeader(template.template_name);
                              setOldValue(template.template_data);
                              setActionID(option.action_id);
                            }}
                          >
                            <span>Edit</span>
                          </button>
                          {(settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) !== 1 && (
                            <button
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: dangerColor,
                                background: dangerBg,
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                transition: 'all 0.2s ease',
                              }}
                              onClick={() => {
                                setSettingsType('text_list');
                                setSettingsSubType('remove');
                                setModalOpen(true);
                                setOldHeader(template.template_name);
                                setOldValue(template.template_data);
                                setActionID(option.action_id);
                              }}
                            >
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CommentsSettings;
